import * as ax from 'axios';
import {AxiosFailLevel} from './axios-custom-types';
import {NgZone} from '@angular/core';

/**
 * NgZone instance reference used by the axios interceptor.
 * This allows the interceptor to run error handling code within Angular's zone,
 * ensuring that errors are properly caught by Angular's global error handler.
 * @private
 */
let ngZoneInstance: NgZone | null = null;

/**
 * Configures an axios response interceptor with automatic retry logic and Angular Zone integration.
 *
 * This interceptor provides robust error handling for all axios HTTP requests throughout the application:
 * - **Automatic Retries**: Failed requests are automatically retried up to a configurable number of times
 * - **Exponential Backoff**: Retry delays increase with each attempt to avoid overwhelming the server
 * - **Zone.js Integration**: Errors are thrown within Angular's zone to ensure proper error handler invocation
 * - **Custom Error Messages**: Failed requests generate descriptive error messages with fail level categorization
 *
 * The interceptor should be initialized once during application startup (typically in APP_INITIALIZER or AppModule)
 * with an NgZone instance to ensure proper Angular integration.
 */
export function setupAxiosRetryInterceptor(ngZone: NgZone): void {
  if (ngZone) {
    ngZoneInstance = ngZone;
  }
  ax.default.interceptors.response.use(
    // success case => return response
    (response) => response,
    // error case => retry axios request or raise error if max retries exceeded
    async (error) => {
      const config = error.config;
      const maxRetries = config.maxRetries || 3;
      const retryDelay = config.retryDelay || 250;
      const failLevel = config.failLevel || AxiosFailLevel.WARNING;
      config._currentRetryCount = config._currentRetryCount || 0;

      // Throw error and reject the request if max retries exceeded
      if (config._currentRetryCount >= maxRetries) {
        const url = config.url || 'unknown';
        const method = config.method?.toUpperCase() || 'REQUEST';
        const errorCode = error?.code || 'UNKNOWN';
        const errorMsg = error?.message || 'Unknown error';
        const customError = `AP_${failLevel}_REQUEST_ERROR: ${method} request to ${url} failed after ${maxRetries} attempts.${errorCode}: ${errorMsg}`;

        // Ensure the error is thrown within Angular's zone; otherwise our
        // ap-errorhandler will not be able to catch the error/warning
        if (ngZoneInstance) {
          ngZoneInstance.run(() => {
            // setTimeout(..., 0) schedules the error to be thrown in the next event loop tick.
            // This is necessary because:
            // 1. We're inside a promise rejection handler, which has special error handling
            // 2. Throwing directly here would be caught by the promise chain, not by Angular's global error handler
            // 3. Deferring to the next tick breaks out of the promise context and allows
            //    Angular's Zone.js to catch the error and route it to the global ErrorHandler
            setTimeout(() => {
              throw new Error(customError);
            }, 0);
          });
        }
        return Promise.reject(customError);
      }

      // Retry axios request with a little delay
      config._currentRetryCount += 1;
      const delay = retryDelay * config._currentRetryCount;
      await new Promise(resolve => setTimeout(resolve, delay));
      return ax.default(config);
    }
  );
}
